import { useEffect, useMemo, useState, useRef, forwardRef } from "react";
import { useBoolean } from "@fluentui/react-hooks";
import { FontIcon, Stack, Text } from "@fluentui/react";
import styles from "./Answer.module.css";
import { AskResponse, Citation, GroundednessData } from "../../api";
import { parseAnswer } from "./AnswerParser";
import { FeedbackButtons } from "../FeedbackButtons/FeedbackButtons";
import { ExpandedModeBadge } from "../ExpandedModeBadge";
import { GroundednessBadge } from "../GroundednessBadge";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import supersub from "remark-supersub";

interface Props {
  answer: AskResponse;
  onCitationClicked: (citedDocument: Citation) => void;
  onSpeak?: any;
  isActive?: boolean;
  index: number;
  conversationId?: string;
  messageId?: string;
  question?: string;
  expandedMode?: boolean;
  groundedness?: GroundednessData | null;
}

const MyStackComponent = forwardRef<HTMLDivElement, any>((props, ref) => (
  <div {...props} ref={ref} />
));

export const Answer = ({
  answer,
  onCitationClicked,
  index,
  conversationId,
  messageId,
  question,
  expandedMode,
  groundedness,
}: Props) => {
  const [isRefAccordionOpen, { toggle: toggleIsRefAccordionOpen }] =
    useBoolean(true);  // Expanded by default
  const filePathTruncationLimit = 50;
  const answerContainerRef = useRef<HTMLDivElement>(null);
  const messageBoxId = "message-" + index;
  const parsedAnswer = useMemo(() => parseAnswer(answer), [answer]);
  const [chevronIsExpanded, setChevronIsExpanded] = useState(true);  // Expanded by default
  const refContainer = useRef<HTMLDivElement>(null);

  const handleChevronClick = () => {
    setChevronIsExpanded(!chevronIsExpanded);
    toggleIsRefAccordionOpen();
  };

  useEffect(() => {
    setChevronIsExpanded(isRefAccordionOpen);
  }, [isRefAccordionOpen]);

  const createCitationFilepath = (
    citation: Citation,
    index: number,
    truncate: boolean = false
  ) => {
    // Prefer metadata.regulation_title when available
    const meta = typeof citation.metadata === "object" && citation.metadata !== null
      ? citation.metadata
      : null;
    const regulationTitle = meta?.regulation_title;
    if (regulationTitle && typeof regulationTitle === "string") {
      return regulationTitle;
    }

    let citationFilename = "";

    if (citation.filepath && citation.chunk_id != null) {
      if (truncate && citation.filepath.length > filePathTruncationLimit) {
        const citationLength = citation.filepath.length;
        citationFilename = `${citation.filepath.substring(0, 20)}...${citation.filepath.substring(citationLength - 20)} - Part ${citation.chunk_id}`;
      } else {
        citationFilename = `${citation.filepath} - Part ${citation.chunk_id}`;
      }
    } else if (citation.title) {
      citationFilename = citation.title;
    } else {
      citationFilename = `Citation ${index}`;
    }
    return citationFilename;
  };

  // Determine citation type for color coding based on title/content
  const getCitationType = (citation: Citation): string => {
    const titleLower = (citation.title || '').toLowerCase();
    const contentLower = (citation.content || '').toLowerCase();
    const combined = titleLower + ' ' + contentLower;

    // Check for regulation numbers (945, 947, etc.)
    if (combined.includes('945') || combined.includes('2019/945')) {
      return styles.citation945;
    }
    if (combined.includes('947') || combined.includes('2019/947')) {
      return styles.citation947;
    }

    // Check for section types
    if (titleLower.includes('definition') || combined.includes('means:') || combined.includes('"means"')) {
      return styles.citationDefinition;
    }
    if (titleLower.includes('article') || titleLower.startsWith('art.') || titleLower.startsWith('art ')) {
      return styles.citationArticle;
    }
    if (titleLower.includes('recital') || titleLower.startsWith('(') && /^\(\d+\)/.test(titleLower)) {
      return styles.citationRecital;
    }
    if (titleLower.includes('annex') || titleLower.includes('appendix')) {
      return styles.citationAnnex;
    }
    if (titleLower.includes('part ') || /^part\s+\d/i.test(titleLower)) {
      return styles.citationPart;
    }

    // Default - no extra class
    return '';
  };

  useEffect(() => {
    const handleCopy = () => {
      alert("Please consider where you paste this content.");
    };
    const messageBox = document.getElementById(messageBoxId);
    messageBox?.addEventListener("copy", handleCopy);
    return () => {
      messageBox?.removeEventListener("copy", handleCopy);
    };
  }, []);

  return (
    <>
      <MyStackComponent
        data-testid="message-box"
        className={styles.answerContainer}
        id={messageBoxId}
        ref={answerContainerRef}
      >
        <Stack.Item grow>
          <div className={styles.answerText}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm, supersub]}
              children={parsedAnswer.markdownFormatText}
            />
          </div>
        </Stack.Item>
        <Stack horizontal className={styles.answerFooter} verticalAlign="start" style={{ padding: "16px 24px" }}>
          <Stack.Item className={styles.answerDisclaimerContainer}>
            <span
              className={`${styles.answerDisclaimer} ${styles.mobileAnswerDisclaimer}`}
            >
              AI-generated content may be incorrect
            </span>
          </Stack.Item>

          {/* Response indicators */}
          <Stack.Item className={styles.indicatorsContainer}>
            {expandedMode && <ExpandedModeBadge />}
            {groundedness && <GroundednessBadge groundedness={groundedness} />}
          </Stack.Item>

          {!!parsedAnswer.citations.length && (
            <Stack.Item aria-label="References">
              <Stack style={{ width: "100%" }}>
                <Stack
                  horizontal
                  horizontalAlign="start"
                  verticalAlign="center"
                  tabIndex={0}
                  onClick={handleChevronClick}
                  role="button"
                  onKeyDown={(e) =>
                    e.key === " " || e.key === "Enter"
                      ? handleChevronClick()
                      : null
                  }
                  data-testid="toggle-citations-list"
                >
                  <Text className={styles.accordionTitle}>
                    <span data-testid="no-of-references">
                      {parsedAnswer.citations.length > 1
                        ? parsedAnswer.citations.length + " references"
                        : "1 reference"}
                    </span>
                  </Text>
                  <FontIcon
                    className={styles.accordionIcon}
                    iconName={
                      chevronIsExpanded ? "ChevronDown" : "ChevronRight"
                    }
                    aria-hidden={false}
                    data-testid="chevron-icon"
                  />
                </Stack>
              </Stack>
            </Stack.Item>
          )}
        </Stack>
        {chevronIsExpanded && (
          <div
            data-testid="citations-container"
            ref={refContainer}
            style={{
              marginTop: 8,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              gap: "8px",
              maxWidth: "100%",
              padding: "0 24px 16px 24px",
            }}
          >
            {parsedAnswer.citations.map((citation, idx) => {
              const citationNum = idx + 1;
              const citationTypeClass = getCitationType(citation);
              return (
                <span
                  data-testid="citation-block"
                  role="button"
                  onKeyDown={(e) =>
                    e.key === " " || e.key === "Enter"
                      ? onCitationClicked(citation)
                      : () => { }
                  }
                  tabIndex={0}
                  title={createCitationFilepath(citation, citationNum)}
                  key={idx}
                  onClick={() => onCitationClicked(citation)}
                  className={styles.citationContainer}
                >
                  <div className={`${styles.citation} ${citationTypeClass}`}>
                    {citationNum}
                  </div>
                  {createCitationFilepath(citation, citationNum, true)}
                </span>
              );
            })}
          </div>
        )}
        <div style={{ padding: "8px 24px 16px 24px" }}>
          {conversationId && messageId && question && (
            <FeedbackButtons
              conversationId={conversationId}
              messageId={messageId}
              question={question}
              answer={answer.answer}
              citations={parsedAnswer.citations}
              expandedMode={expandedMode}
              groundedness={groundedness}
            />
          )}
        </div>
      </MyStackComponent>
    </>
  );
};
